// src/pages/Signup.js
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  sendEmailVerification,
} from "firebase/auth";
import { auth, db, googleProvider } from "../firebase/config";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";

import bgD from "../assets/loginBg2.png";
import {
  FiArrowRight,
  FiArrowLeft,
  FiCheck,
  FiZap,
  FiStar,
  FiLock,
  FiGlobe,
  FiUsers,
  FiTrendingUp,
  FiMessageSquare,
  FiUser,
  FiMail,
  FiUserCheck,
  FiTarget,
  FiDollarSign,
  FiCalendar,
  FiPieChart,
  FiBook,
  FiHeart,
  FiBriefcase,
  FiHome,
} from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";

// ----------------------------------------------------------------
// --- STYLED COMPONENTS ---
// ----------------------------------------------------------------

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  overflow: hidden;
  transition: 0.5s;
  background-color: #1b2130;

  @media (max-width: 1450px) {
    background: url(${bgD});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    flex-direction: column;
  }

  @media (max-width: 768px) {
    /* background: #1b2130; */
  }
`;

const Container = styled.div`
  width: 40%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1b2130;
  color: white;
  transition: 0.5s;

  @media (max-width: 1450px) {
    width: 100%;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(8px);
    min-height: 100vh;
    padding: 20px;
  }

  @media (max-width: 1024px) {
    padding: 15px;
  }

  @media (max-width: 768px) {
    /* background: var(--secondaryBackgroundColor); */
    margin-top: -10%;
    padding: 10px;
    min-height: calc(100vh - 60px);
  }

  @media (max-width: 480px) {
    padding: 5px;
  }
`;

const Box = styled.div`
  position: relative;
  overflow: hidden;
  background: #313a5069;
  padding: 40px 30px;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  text-align: center;
  box-shadow: 0 2px 10px #313a5069;
  backdrop-filter: blur(15px);
  transition: all 0.3s ease;

  /* ⬇️ Force content below glow */
  > * {
    position: relative;
    z-index: 1;
  }

  /* 🔥 Circular gradient glow (always on top) */
  &::before {
    content: "";
    position: absolute;
    top: -60px;
    right: -100px;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle,
      rgba(45, 136, 211, 0.35),
      rgba(255, 255, 255, 0) 70%
    );
    pointer-events: none;
    filter: blur(10px);
    z-index: 2;
  }

  @media (max-width: 1450px) {
    width: 90%;
    max-width: 600px;
    margin: 0 auto;
  }

  @media (max-width: 1024px) {
    width: 95%;
    padding: 35px 25px;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 30px 20px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 480px) {
    padding: 25px 15px;
    border-radius: 8px;
    margin: 0;
    box-shadow: none;
  }

  @media (max-width: 360px) {
    padding: 20px 12px;
  }
`;

const ProgressBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 20px;
    left: 10%;
    right: 10%;
    height: 2px;
    background: #1b2130;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 60px;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  background: ${({ active }) =>
    active ? "linear-gradient(45deg, #495269, #333)" : "  #1b2130;"};
  color: ${({ active }) => (active ? "white" : "var(--fontSecondaryColor)")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 0.85rem;
    margin-bottom: 0.3rem;
  }
`;

const StepLabel = styled.span`
  font-size: 0.75rem;
  color: var(--fontSecondaryColor);
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 0.7rem;
  }

  @media (max-width: 480px) {
    font-size: 0.65rem;
  }
`;

const Title = styled.div`
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.6rem;
    margin-bottom: 0.5rem;
    color: var(--primaryColor);
    font-weight: 600;
  }

  p {
    color: var(--fontSecondaryColor);
    font-size: 0.85rem;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.2rem;

    h2 {
      font-size: 1.4rem;
    }

    p {
      font-size: 0.8rem;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;

    h2 {
      font-size: 1.2rem;
    }

    p {
      font-size: 0.75rem;
    }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(255, 107, 107, 0.1);
  color: #ff6b6b;
  padding: 10px 16px;
  border-radius: 10px;
  margin-bottom: 1.2rem;
  border-left: 3px solid #ff6b6b;
  text-align: left;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    padding: 8px 14px;
    margin-bottom: 1rem;
    font-size: 0.8rem;
  }

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 0.75rem;
  }
`;

const VerificationNotice = styled.div`
  background: rgba(73, 82, 105, 0.1);
  color: #a8a8a8;
  padding: 10px 16px;
  border-radius: 10px;
  margin-bottom: 1.2rem;
  border-left: 3px solid #495269;
  text-align: left;
  font-size: 0.85rem;

  p:first-child {
    margin: 0 0 5px 0;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p:last-child {
    margin: 0;
    font-size: 0.8rem;
  }

  @media (max-width: 768px) {
    padding: 8px 14px;
    margin-bottom: 1rem;
    font-size: 0.8rem;

    p:last-child {
      font-size: 0.75rem;
    }
  }

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 0.75rem;

    p:last-child {
      font-size: 0.7rem;
    }
  }
`;

const StepContainer = styled.div`
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0.8rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 0.6rem;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--fontSecondaryColor);
  font-size: 1.1rem;

  @media (max-width: 768px) {
    left: 14px;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    left: 12px;
    font-size: 0.9rem;
  }
`;

const Input = styled.input`
  padding: 20px 16px 20px 48px;
  width: 100%;
  border-radius: 12px;
  border: 2px solid transparent;
  font-family: "Montserrat", sans-serif;
  font-size: 0.9rem;
  background: #1b2130;
  outline: none;
  color: white;
  box-shadow: 0 2px 8px #313a5069;
  transition: all 0.3s ease;

  &:focus {
    border-color: #495269;
    box-shadow: 0 0 0 3px rgba(73, 82, 105, 0.1);
  }

  &::placeholder {
    color: #a8a8a8;
    font-size: 0.85rem;
  }

  @media (max-width: 768px) {
    padding: 14px 14px 14px 44px;
    font-size: 0.85rem;
    border-radius: 10px;

    &::placeholder {
      font-size: 0.8rem;
    }
  }

  @media (max-width: 480px) {
    padding: 12px 12px 12px 40px;
    font-size: 0.8rem;
    border-radius: 8px;

    &::placeholder {
      font-size: 0.75rem;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1rem;
  text-align: left;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    color: white;
    font-weight: 600;
  }

  svg {
    color: #495269;
    font-size: 1.2rem;
  }

  @media (max-width: 768px) {
    gap: 6px;
    margin-bottom: 0.8rem;

    h3 {
      font-size: 1rem;
    }

    svg {
      font-size: 1.1rem;
    }
  }

  @media (max-width: 480px) {
    gap: 5px;
    margin-bottom: 0.6rem;

    h3 {
      font-size: 0.95rem;
    }

    svg {
      font-size: 1rem;
    }
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr); /* Keep 2 columns on mobile */
    gap: 0.6rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr); /* Still 2 columns on very small */
    gap: 0.5rem;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr; /* Only switch to 1 column on tiny screens */
    gap: 0.4rem;
  }
`;

const OptionCard = styled.div`
  background: ${({ selected }) =>
    selected ? "rgba(73, 82, 105, 0.2)" : "#1b2130"};
  border: 2px solid ${({ selected }) => (selected ? "#495269" : "transparent")};
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  text-align: center;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${({ selected }) =>
      selected ? "#495269" : "rgba(73, 82, 105, 0.3)"};
  }

  @media (max-width: 768px) {
    padding: 0.8rem;
    border-radius: 10px;
    min-height: 90px;
  }

  @media (max-width: 480px) {
    padding: 0.7rem;
    border-radius: 8px;
    min-height: 85px;
  }
`;

const OptionIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #495269;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 0.4rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 0.3rem;
  }
`;

const OptionLabel = styled.div`
  font-weight: 600;
  color: white;
  font-size: 0.85rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const OptionCheck = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  background: #495269;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    font-size: 0.6rem;
  }

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
    top: 6px;
    right: 6px;
  }
`;

// Multi-select counter badge
const SelectedCountBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(45deg, #495269, #333);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    font-size: 0.6rem;
  }

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
    top: 6px;
    right: 6px;
  }
`;

const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.8rem;
  color: white;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const PlanTag = styled.p`
  font-size: 0.8rem;
  color: #a8a8a8;
  margin-top: 0.3rem;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const Feature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0.5rem;
  font-size: 0.8rem;
  color: white;
  line-height: 1.3;

  svg {
    color: #4caf50;
    min-width: 16px;
    margin-top: 2px;
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 0.4rem;
    gap: 6px;

    svg {
      min-width: 14px;
    }
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
    margin-bottom: 0.3rem;
    gap: 5px;

    svg {
      min-width: 12px;
    }
  }
`;

const PlanCard = styled.div`
  background: var(--secondaryBackgroundColor);
  border: 2px solid ${({ selected }) => (selected ? "#495269" : "transparent")};
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1.2rem;
    border-radius: 14px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const ProPlanCard = styled(PlanCard)`
  background: linear-gradient(135deg, #495269, #2d3447);
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ff6b8b, #ffa500);
  }

  ${PlanName}, ${PlanTag}, ${Feature} {
    color: white;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(73, 82, 105, 0.3);
  }

  @media (max-width: 600px) {
    order: -1; /* Move Pro plan to top on mobile */
  }
`;

const ProBadge = styled.div`
  position: absolute;
  top: 15px;
  right: -35px;
  background: linear-gradient(45deg, #ff6b8b, #ffa500);
  color: white;
  padding: 6px 35px;
  transform: rotate(45deg);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 5px 30px;
    font-size: 0.65rem;
    top: 12px;
    right: -32px;
  }

  @media (max-width: 480px) {
    padding: 4px 25px;
    font-size: 0.6rem;
    top: 10px;
    right: -28px;
  }
`;

const PlanHeader = styled.div`
  margin-bottom: 1.5rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const PlanPrice = styled.div`
  margin-bottom: 0.8rem;
`;

const Price = styled.span`
  font-size: 2rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const Period = styled.span`
  font-size: 0.9rem;
  color: #a8a8a8;
  margin-left: 4px;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
  text-align: left;
  flex-grow: 1;

  @media (max-width: 768px) {
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const SelectButton = styled.div`
  background: ${({ selected }) => (selected ? "#4CAF50" : "#1b2130")};
  color: ${({ selected }) => (selected ? "white" : "white")};
  border: 2px solid ${({ selected }) => (selected ? "#4CAF50" : "#1b2130")};
  padding: 12px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  text-align: center;
  flex-shrink: 0;

  &:hover {
    background: ${({ selected }) =>
      selected ? "#45a049" : "rgba(73, 82, 105, 0.1)"};
  }

  @media (max-width: 768px) {
    padding: 10px;
    font-size: 0.85rem;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 8px;
    font-size: 0.8rem;
  }
`;
const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Button = styled.button`
  background: #495269;
  color: white;
  border: none;
  width: 100%;
  font-family: "Montserrat", sans-serif;
  padding: 14px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(73, 82, 105, 0.3);
    background: #3d4558;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 0.85rem;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 0.8rem;
    border-radius: 8px;
  }
`;

const BackButton = styled(Button)`
  background: #1b2130;
  color: white;

  &:hover {
    background: rgba(73, 82, 105, 0.1);
    transform: translateY(-2px);
  }
`;

const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 0.8rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    gap: 0.6rem;
    margin-top: 1.2rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-top: 1rem;
    grid-template-columns: 1fr 1.5fr;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: #a8a8a8;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #1b2130;
  }

  span {
    padding: 0 0.8rem;
    font-size: 0.85rem;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    margin: 1.2rem 0;

    span {
      font-size: 0.8rem;
      padding: 0 0.6rem;
    }
  }

  @media (max-width: 480px) {
    margin: 1rem 0;

    span {
      font-size: 0.75rem;
      padding: 0 0.5rem;
    }
  }
`;

const SocialBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: #394155;
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  font-family: "Montserrat";

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    background: rgba(73, 82, 105, 0.1);
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 0.85rem;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 0.8rem;
    border-radius: 8px;
  }
`;

const LoginLink = styled.p`
  margin-top: 1.5rem;
  color: #a8a8a8;
  font-size: 0.85rem;

  a {
    color: #495269;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    margin-top: 1.2rem;
    font-size: 0.8rem;
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
    font-size: 0.75rem;
  }
`;

const ImageSection = styled.div`
  width: 60%;
  background: url(${bgD});
  background-size: cover;
  background-position: center;
  transition: 0.5s;
  position: relative;

  @media (max-width: 1450px) {
    display: none;
  }
`;

const MultiSelectHelper = styled.p`
  font-size: 0.8rem;
  color: #a8a8a8;
  margin-top: 0.5rem;
  text-align: left;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

// ----------------------------------------------------------------
// --- COMPONENT CODE ---
// ----------------------------------------------------------------

// PACKAGES
const PACKAGES = {
  FREE: "FREE",
  PRO: "PRO",
};

// Personal motivation options (multi-select)
const MOTIVATION_OPTIONS = [
  {
    id: "discipline",
    label: "To be more disciplined & productive",
    icon: FiTarget,
    description: "Build better habits and stay focused",
  },
  {
    id: "finances",
    label: "Manage my finances better",
    icon: FiDollarSign,
    description: "Track expenses and budget effectively",
  },
  {
    id: "time",
    label: "Better time management",
    icon: FiCalendar,
    description: "Organize my schedule and priorities",
  },
  {
    id: "goals",
    label: "Achieve personal goals",
    icon: FiStar,
    description: "Plan and track my life objectives",
  },

  {
    id: "worklife",
    label: "Better work-life balance",
    icon: FiHome,
    description: "Separate professional and personal life",
  },
  {
    id: "analytics",
    label: "Track my progress with analytics",
    icon: FiPieChart,
    description: "See data and insights about my life",
  },
];

export default function Signup({ theme }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    motivations: [],
    referral: "",
    package: PACKAGES.FREE, // Default to FREE
  });
  const [error, setError] = useState("");
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Signup - ASites Planner";
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  // Handle multi-select for motivations
  const toggleMotivation = (motivationId) => {
    setFormData((prev) => {
      const currentMotivations = [...prev.motivations];
      if (currentMotivations.includes(motivationId)) {
        // Remove if already selected
        return {
          ...prev,
          motivations: currentMotivations.filter((id) => id !== motivationId),
        };
      } else {
        // Add if not selected (limit to maybe 5 selections max)
        if (currentMotivations.length < 5) {
          return {
            ...prev,
            motivations: [...currentMotivations, motivationId],
          };
        }
        return prev;
      }
    });
    setError("");
  };

  const nextStep = async () => {
    // Validate current step
    if (step === 1) {
      if (!formData.name.trim())
        return setError("Please enter your full name.");
      if (!formData.email.trim())
        return setError("Please enter a valid email.");
      if (formData.password.length < 6)
        return setError("Password must be at least 6 characters.");

      setStep(2);
    }

    if (step === 2) {
      if (formData.motivations.length === 0)
        return setError("Please select at least one motivation.");

      // Automatically create account and go to upgrade page
      await handleAutoCreateAccount();
    }
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  // New function to automatically create account with email verification
  const handleAutoCreateAccount = async () => {
    setIsCreatingAccount(true);
    setError("");

    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      await updateProfile(user, { displayName: formData.name });

      // Send email verification
      await sendEmailVerification(user);

      // Save user data with FREE plan by default
      await setDoc(doc(db, "users", user.uid), {
        name: formData.name,
        email: formData.email,
        createdAt: new Date(),
        subscriptionTier: PACKAGES.FREE, // Always start with FREE
        motivations: formData.motivations,
        referralSource: formData.referral || "direct",
        registrationStep: "completed",
        needsUpgradePrompt: true, // Flag to show upgrade prompt
        emailVerified: false, // Track verification status
      });

      // Redirect to upgrade page instead of /app
      navigate("/upgrade", {
        state: {
          email: formData.email,
          name: formData.name,
          motivations: formData.motivations,
          justRegistered: true,
          emailVerified: false, // Add flag to show verification reminder
        },
      });
    } catch (err) {
      // Handle specific Firebase auth errors
      let errorMessage = "An error occurred during signup. Please try again.";

      switch (err.code) {
        case "auth/email-already-in-use":
          errorMessage =
            "This email address is already registered. Please use a different email or try logging in.";
          break;
        case "auth/invalid-email":
          errorMessage = "Please enter a valid email address.";
          break;
        case "auth/operation-not-allowed":
          errorMessage =
            "Email/password accounts are not enabled. Please contact support.";
          break;
        case "auth/weak-password":
          errorMessage =
            "Password is too weak. Please use at least 6 characters with a mix of letters and numbers.";
          break;
        case "auth/network-request-failed":
          errorMessage =
            "Network error. Please check your internet connection and try again.";
          break;
        case "auth/too-many-requests":
          errorMessage =
            "Too many unsuccessful attempts. Please try again later.";
          break;
        default:
          errorMessage = err.message || "Signup failed. Please try again.";
      }

      setError(errorMessage);
      setIsCreatingAccount(false);
    }
  };

  async function handleGoogleSignup() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if email is verified
      if (!user.emailVerified) {
        // Optionally send verification email for Google accounts too
        await sendEmailVerification(user);
      }

      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        await setDoc(ref, {
          name: user.displayName,
          email: user.email,
          createdAt: new Date(),
          subscriptionTier: PACKAGES.FREE, // Google signup also gets FREE
          motivations: [],
          referralSource: "google",
          registrationStep: "completed",
          needsUpgradePrompt: true,
          emailVerified: user.emailVerified,
        });
      }

      // Redirect to upgrade page for Google signup too
      navigate("/upgrade", {
        state: {
          email: user.email,
          name: user.displayName,
          justRegistered: true,
          fromGoogle: true,
          emailVerified: user.emailVerified,
        },
      });
    } catch (err) {
      // Handle Google signup errors
      let errorMessage = "Google signup failed. Please try again.";

      switch (err.code) {
        case "auth/account-exists-with-different-credential":
          errorMessage =
            "An account already exists with this email using a different sign-in method. Please try logging in with email/password.";
          break;
        case "auth/popup-closed-by-user":
          errorMessage = "Signup was cancelled. Please try again.";
          break;
        case "auth/unauthorized-domain":
          errorMessage = "This domain is not authorized for Google sign-in.";
          break;
        case "auth/network-request-failed":
          errorMessage =
            "Network error. Please check your internet connection.";
          break;
        default:
          errorMessage = err.message || "Google signup failed.";
      }

      setError(errorMessage);
    }
  }

  return (
    <Wrapper theme={theme}>
      <Container>
        <Box>
          {/* Progress Steps - Now only 2 steps */}
          <ProgressBar>
            {[1, 2].map((num) => (
              <ProgressStep key={num} active={step >= num}>
                <StepNumber active={step >= num}>{num}</StepNumber>
                <StepLabel>
                  {num === 1 && "Account"}
                  {num === 2 && "Motivation"}
                </StepLabel>
              </ProgressStep>
            ))}
          </ProgressBar>

          <Title>
            <h2>Create Your Account</h2>
            <p>Step {step} of 2</p>
          </Title>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {/* Email Verification Notice - Only show in step 2 when no errors */}
          {step === 2 && !error && (
            <VerificationNotice>
              <p>
                <FiMail />
                Email Verification
              </p>
              <p>
                We'll send a verification email to{" "}
                <strong>{formData.email}</strong>. Please check your inbox to
                verify your account.
              </p>
            </VerificationNotice>
          )}

          {/* Step 1: Basic Info */}
          {step === 1 && (
            <StepContainer>
              <InputGroup>
                <InputIcon>
                  <FiUser />
                </InputIcon>
                <Input
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                />
              </InputGroup>

              <InputGroup>
                <InputIcon>
                  <FiMail />
                </InputIcon>
                <Input
                  placeholder="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
              </InputGroup>

              <InputGroup>
                <InputIcon>
                  <FiLock />
                </InputIcon>
                <Input
                  type="password"
                  placeholder="Password (min. 6 characters)"
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                />
              </InputGroup>

              <Button onClick={nextStep} type="button">
                Continue <FiArrowRight />
              </Button>
            </StepContainer>
          )}

          {/* Step 2: Personal Motivation (Multi-select) */}
          {step === 2 && (
            <StepContainer>
              <Section>
                <SectionTitle>
                  <FiTarget style={{ color: "var(--fontPrimaryColor)" }} />
                  <h3>What motivated you to choose ASites Planner?</h3>
                </SectionTitle>
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "#a8a8a8",
                    textAlign: "left",
                    marginBottom: "1rem",
                  }}
                >
                  Select all that apply (you can choose multiple):
                </p>

                <OptionsGrid>
                  {MOTIVATION_OPTIONS.map((option) => {
                    const isSelected = formData.motivations.includes(option.id);
                    return (
                      <OptionCard
                        key={option.id}
                        selected={isSelected}
                        onClick={() => toggleMotivation(option.id)}
                      >
                        <OptionIcon
                          style={{ color: "var(--fontSecondaryColor)" }}
                        >
                          {React.createElement(option.icon)}
                        </OptionIcon>
                        <OptionLabel>{option.label}</OptionLabel>

                        {isSelected && (
                          <OptionCheck>
                            <FiCheck />
                          </OptionCheck>
                        )}

                        {/* Show number if selected and has multiple selections */}
                        {isSelected && formData.motivations.length > 1 && (
                          <SelectedCountBadge>
                            {formData.motivations.indexOf(option.id) + 1}
                          </SelectedCountBadge>
                        )}
                      </OptionCard>
                    );
                  })}
                </OptionsGrid>

                <MultiSelectHelper>
                  {formData.motivations.length === 0
                    ? "Select at least one option that describes your motivation"
                    : `Selected ${formData.motivations.length} motivation${
                        formData.motivations.length > 1 ? "s" : ""
                      }`}
                </MultiSelectHelper>
              </Section>

              <ButtonRow>
                <BackButton onClick={prevStep} type="button">
                  <FiArrowLeft /> Back
                </BackButton>
                <Button
                  onClick={nextStep}
                  type="button"
                  disabled={isCreatingAccount}
                >
                  {isCreatingAccount ? (
                    <>
                      <Spinner />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account <FiArrowRight />
                    </>
                  )}
                </Button>
              </ButtonRow>
            </StepContainer>
          )}

          {/* Google Signup */}
          {step === 1 && (
            <>
              <Divider>
                <span>or continue with</span>
              </Divider>

              <SocialBtn onClick={handleGoogleSignup}>
                <FcGoogle size={20} /> Sign up with Google
              </SocialBtn>
            </>
          )}

          <LoginLink>
            Already have an account?{" "}
            <Link to="/login" style={{ color: "white" }}>
              Log In
            </Link>
          </LoginLink>
        </Box>
      </Container>

      <ImageSection theme={theme} />
    </Wrapper>
  );
}
